import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { Lock, Building2, ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react'
import { Button, Input } from '../atoms'
import { useResetPassword } from '../../hooks/usePasswordReset'
import { useErrorToast, useSuccessToast } from '../atoms/Toaster'

function getPasswordStrength(pw: string): { score: number; label: string; color: string } {
  if (pw.length < 8) return { score: 0, label: 'Muy débil', color: 'bg-red-500' }
    let score = 0
    if (pw.length >= 8) score++
    if (pw.length >= 12) score++
    if (/[A-Z]/.test(pw)) score++
    if (/[0-9]/.test(pw)) score++
    if (/[^A-Za-z0-9]/.test(pw)) score++
  if (score <= 1) return { score: 1, label: 'Débil', color: 'bg-red-500' }
  if (score <= 2) return { score: 2, label: 'Aceptable', color: 'bg-amber-500' }
  if (score <= 3) return { score: 3, label: 'Buena', color: 'bg-blue-500' }
  return { score: 4, label: 'Fuerte', color: 'bg-emerald-500' }
}

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') || ''

  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [success, setSuccess] = useState(false)

  const resetMutation = useResetPassword()
  const showError = useErrorToast()
  const showSuccess = useSuccessToast()

  useEffect(() => {
    if (!token) {
      showError('Enlace inválido', 'El enlace de restablecimiento no contiene un token válido')
    }
  }, [token, showError])

  const strength = getPasswordStrength(newPassword)
  const passwordsMatch = newPassword.length > 0 && newPassword === confirmPassword
  const canSubmit = newPassword.length >= 8 && passwordsMatch && !resetMutation.isPending

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!canSubmit) return
    try {
      const result = await resetMutation.mutateAsync({ token, newPassword })
      setSuccess(true)
      showSuccess('Contraseña restablecida', result.message)
    } catch (error: any) {
      showError('Error', error.message || 'No se pudo restablecer la contraseña')
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">¡Contraseña restablecida!</h2>
            <p className="mt-2 text-sm text-gray-600">
              Tu contraseña ha sido actualizada exitosamente. Ya puedes iniciar sesión con tu nueva contraseña.
            </p>
          </div>

          <div className="card p-6">
            <Button
              type="button"
              className="w-full"
              onClick={() => navigate('/login')}
            >
              Ir a iniciar sesión
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="flex justify-center">
              <Building2 className="h-12 w-12 text-red-500" />
            </div>
            <h2 className="mt-6 text-2xl font-bold text-gray-900">Enlace inválido</h2>
            <p className="mt-2 text-sm text-gray-600">
              El enlace de restablecimiento no es válido o expiró. Solicita uno nuevo.
            </p>
          </div>
          <div className="card p-6 space-y-3">
            <Link to="/forgot-password" className="w-full btn-primary justify-center">
              Solicitar nuevo enlace
            </Link>
            <Link to="/login" className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1">
              <ArrowLeft className="w-3 h-3" /> Volver al inicio de sesión
            </Link>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center">
            <Building2 className="h-12 w-12 text-primary-600" />
          </div>
          <h2 className="mt-6 text-2xl font-bold text-gray-900">Restablecer contraseña</h2>
          <p className="mt-2 text-sm text-gray-600">
            Crea una nueva contraseña para tu cuenta.
          </p>
        </div>

        <form className="card p-6 space-y-4" onSubmit={handleSubmit}>
          <div className="relative">
            <Input
              label="Nueva contraseña"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={resetMutation.isPending}
              leftIcon={<Lock className="w-4 h-4 text-gray-400" />}
              minLength={8}
            />
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              className="absolute right-3 top-9 text-gray-400 hover:text-gray-600"
              tabIndex={-1}
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
            {newPassword.length > 0 && (
              <div className="mt-2">
                <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                  <span>Seguridad: {strength.label}</span>
                </div>
                <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${strength.color} transition-all`}
                    style={{ width: `${(strength.score / 4) * 100}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <Input
            label="Confirmar contraseña"
            type={showPassword ? 'text' : 'password'}
            placeholder="••••••••"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            disabled={resetMutation.isPending}
            leftIcon={<Lock className="w-4 h-4 text-gray-400" />}
            minLength={8}
          />
          {confirmPassword.length > 0 && !passwordsMatch && (
            <p className="text-xs text-red-600 -mt-2">Las contraseñas no coinciden</p>
          )}

          <Button
            type="submit"
            className="w-full"
            isLoading={resetMutation.isPending}
            disabled={!canSubmit}
          >
            Restablecer contraseña
          </Button>

          <Link
            to="/login"
            className="w-full text-sm text-gray-500 hover:text-gray-700 flex items-center justify-center gap-1"
          >
            <ArrowLeft className="w-3 h-3" /> Volver al inicio de sesión
          </Link>
        </form>
      </div>
    </div>
  )
}
